// Reducer del editor de flujo. Vive aparte del hook para poder probarlo: es la
// pieza con mas logica (deshacer, arrastre, recalculo de bucles) y dentro de un
// hook no hay forma de ejercitarla sin montar React.
//
// Aqui no hay efectos ni peticiones: entra un estado y una accion, sale un
// estado. El guardado lo lleva useFlowEditor.
import {
  autoLayout, detectCycles, fitView, snap, zoomAt, panDesde, rectDeDosPuntos, nodosEnRect,
} from "./flowGeometry";

const HIST_MAX = 30;
export const DEBOUNCE_POS = 600;

export const inicial = {
  nodes: {},          // { [id]: tarea }
  order: [],          // ids en orden de pintado; el ultimo va encima
  edges: {},          // { [id]: dependencia }
  sel:   { nodes: [], edge: null },
  view:  { x: 0, y: 0, k: 1 },
  drag:  null,
  hist:  { past: [], future: [] },
  dirty: {},          // { [nodeId]: true } posiciones pendientes de guardar
  cycles: [],         // ids de arista que cierran bucle
  anim:   null,       // {desde,hasta,t} mientras se reorganiza
  listo: false,       // ya se ha colocado el grafo al menos una vez
};

// El historial guarda OPERACIONES, no fotos del estado.
//
// Con fotos, deshacer solo cambiaba la pantalla: crear y borrar ya estan
// guardados en el servidor, asi que al deshacer una creacion el nodo seguia
// existiendo en la base de datos y volvia en el siguiente guardado, y al
// deshacer un borrado aparecia un nodo fantasma que ya no existia y que el
// siguiente refresco se llevaba. Eso es lo que daba errores y perdia
// informacion.
//
// Ahora cada operacion sabe como desandarse CONTRA EL SERVIDOR, y el hook la
// ejecuta. Las que hay:
//
//   mover          { pos: { id: { antes:{x,y}, despues:{x,y} } } }
//   crearNodo      { id }              deshacer = borrarlo
//   crearArista    { id }              deshacer = borrarla
//   borrarArista   { edge }            deshacer = volver a crearla
//
// Borrar un NODO no entra en el historial, y ademas lo vacia. No es pereza: al
// borrarlo, la base de datos ya ha desenganchado las horas del cuaderno que
// colgaban de ese paso (ON DELETE SET NULL), asi que volver a crearlo daria un
// paso distinto con otro id y sin esas horas. Prometer que eso es "deshacer"
// seria mentir, y por eso el borrado pide confirmacion.
const conOp = (st, siguiente, op) => ({
  ...siguiente,
  hist: {
    past: [...st.hist.past.slice(-(HIST_MAX - 1)), op],
    future: [],
  },
});

const recalcularCiclos = st => ({ ...st, cycles: detectCycles(st.nodes, st.edges) });

export function reducer(st, a) {
  switch (a.type) {
    // Llega el grafo del servidor. Ojo: esto NO pasa solo al abrir el proyecto,
    // pasa cada vez que se guarda cualquier campo, porque el hook de datos
    // devuelve un array de tareas nuevo. Asi que aqui no se puede reiniciar
    // nada que el usuario tenga en marcha:
    //
    //   - La SELECCION se conserva. Reiniciarla cerraba el panel de propiedades
    //     en mitad de escribir, justo cuando saltaba el debounce de 600 ms.
    //   - Las posiciones MOVIDAS Y SIN GUARDAR mandan sobre las del servidor, o
    //     guardar un titulo devolvia el nodo a su sitio anterior.
    //   - El historial de deshacer tampoco se tira.
    //   - El ARRASTRE en curso se conserva. Antes se ponia a null, asi que un
    //     guardado de fondo que aterrizara a mitad de gesto lo cancelaba: el
    //     nodo se quedaba clavado y volvia a su posicion del servidor. Es el
    //     mismo error que los otros tres, escrito justo debajo del comentario
    //     que dice que no se haga.
    case "INIT": {
      const nodes = {}, llegados = [];
      // Lo que se esta arrastrando AHORA MISMO cuenta como local aunque no este
      // todavia en `dirty`: dirty lo marca DRAG_END, o sea al soltar. Sin esto,
      // conservar el arrastre no servia de nada —un guardado de fondo a mitad
      // de gesto devolvia el nodo a su posicion del servidor y el paso saltaba
      // debajo del cursor, que es el fallo que se veia al mover un paso—.
      const arrastrados = st.drag?.mode === "node"
        ? new Set((st.drag.ids || []).map(String)) : null;
      for (const t of a.tareas || []) {
        const id = String(t.id);
        const local = (st.dirty[id] || arrastrados?.has(id)) ? st.nodes[id] : null;
        nodes[id] = local ? { ...t, pos_x: local.pos_x, pos_y: local.pos_y } : t;
        llegados.push(id);
      }
      const edges = {};
      for (const e of a.dependencias || []) edges[String(e.id)] = e;

      // El orden de pintado de los que ya estaban se respeta (quien estaba
      // delante sigue delante) y lo nuevo se apila al final.
      const previos = st.order.filter(id => nodes[id]);
      const yaEstaban = new Set(previos);
      const order = [...previos, ...llegados.filter(id => !yaEstaban.has(id))];

      return recalcularCiclos({
        ...st,
        nodes, order, edges,
        sel: {
          nodes: st.sel.nodes.filter(id => nodes[id]),
          edge:  st.sel.edge && edges[st.sel.edge] ? st.sel.edge : null,
        },
        // Se conserva SOLO si todo lo que arrastra sigue existiendo. Si alguien
        // ha borrado uno de esos pasos desde otra sesion, el DRAG_MOVE
        // siguiente escribiria sobre un nodo inexistente y lo resucitaria como
        // un objeto a medias: `{...undefined, pos_x, pos_y}`.
        drag: st.drag && (st.drag.mode !== "node"
          || (st.drag.ids || []).every(id => nodes[id])) ? st.drag : null,
        view: st.listo ? st.view : inicial.view,
      });
    }

    // ── Vista ────────────────────────────────────────────────────────────────
    case "SET_VIEW":  return { ...st, view: a.view, listo: true };
    case "ZOOM":      return { ...st, view: zoomAt(st.view, a.cx, a.cy, a.factor, a.rect), listo: true };
    case "ZOOM_CENTRO": {
      const cx = a.rect.left + a.rect.width / 2, cy = a.rect.top + a.rect.height / 2;
      return { ...st, view: zoomAt(st.view, cx, cy, a.factor, a.rect), listo: true };
    }
    // `tapadoIzq` lo pone el lienzo: es el ancho del panel cuando esta abierto.
    // Encajar sin contarlo metia medio diagrama debajo del panel.
    case "FIT":       return { ...st, view: fitView(st.nodes, a.rect, 48, a.tapadoIzq || 0), listo: true };

    // Centrar la vista en unos nodos concretos. Lo usa la confirmacion antes de
    // preguntar: si te van a decir "se llevara 2 conexiones", lo primero es
    // poder VER de que paso se habla. Reutiliza fitView sobre un subconjunto,
    // con mucho margen para que el nodo quede centrado y no llenando la
    // pantalla —fitView ya topa el zoom en 1,2—.
    case "CENTRAR": {
      const sub = {};
      for (const id of (a.ids || []).map(String)) if (st.nodes[id]) sub[id] = st.nodes[id];
      if (!Object.keys(sub).length) return st;
      return { ...st, view: fitView(sub, a.rect, a.pad ?? 170, a.tapadoIzq || 0), listo: true };
    }

    // ── Seleccion ────────────────────────────────────────────────────────────
    case "SEL_NODE": {
      const id = String(a.id);
      // Al seleccionar, el nodo pasa al final del orden de pintado: en SVG no
      // hay z-index, asi que "traer al frente" es reordenar el documento.
      const order = [...st.order.filter(x => x !== id), id];
      // Con Shift o Ctrl se SUMA a la seleccion, y se quita si ya estaba. Es
      // lo que permite corregir una marquesina sin volver a barrer: quitar el
      // nodo que ha entrado de mas.
      if (a.sumar) {
        const dentro = st.sel.nodes.includes(id);
        const nodes = dentro ? st.sel.nodes.filter(x => x !== id) : [...st.sel.nodes, id];
        return { ...st, order, sel: { nodes, edge: null } };
      }
      return { ...st, order, sel: { nodes: [id], edge: null } };
    }
    // Varios de golpe (lo que deja una marquesina). Todos pasan al frente
    // conservando su orden relativo.
    case "SEL_MANY": {
      const ids = (a.ids || []).map(String).filter(id => st.nodes[id]);
      const base = a.sumar ? st.sel.nodes : [];
      const nodes = [...new Set([...base, ...ids])];
      const order = [...st.order.filter(x => !nodes.includes(x)), ...st.order.filter(x => nodes.includes(x))];
      return { ...st, order, sel: { nodes, edge: null } };
    }
    case "SEL_EDGE":  return { ...st, sel: { nodes: [], edge: String(a.id) } };
    case "CLEAR_SEL": return { ...st, sel: { nodes: [], edge: null } };

    // Desplazar la vista sin arrastrar: las flechas del teclado. Los pixeles se
    // dividen por k porque view esta en unidades de lienzo, no de pantalla: a
    // la mitad de zoom, la misma tecla tiene que recorrer el doble de lienzo
    // para que el gesto se sienta igual en la pantalla.
    case "PAN": {
      return { ...st, listo: true, view: {
        ...st.view,
        x: st.view.x + (a.dxPx || 0) / st.view.k,
        y: st.view.y + (a.dyPx || 0) / st.view.k,
      } };
    }

    // ── Arrastre ─────────────────────────────────────────────────────────────
    case "DRAG_START": return { ...st, drag: a.drag };
    case "DRAG_MOVE": {
      if (!st.drag) return st;
      if (st.drag.mode === "pan")
        return { ...st, view: panDesde(st.drag.view0, st.view.k, a.dxPx, a.dyPx), listo: true };
      if (st.drag.mode === "node") {
        const nodes = { ...st.nodes };
        for (const id of st.drag.ids) {
          const g = st.drag.grab[id];
          if (!g) continue;
          nodes[id] = { ...nodes[id], pos_x: a.p.x - g.dx, pos_y: a.p.y - g.dy };
        }
        // La arista que el nodo esta tapando, si la hay: al soltar ahi se
        // intercala en ella. Lo calcula el lienzo, que es quien sabe donde ha
        // quedado el centro del nodo; aqui solo se guarda para pintarlo.
        const intercalar = a.intercalar ?? null;
        const drag = String(st.drag.intercalar ?? "") === String(intercalar ?? "")
          ? st.drag : { ...st.drag, intercalar };
        return { ...st, nodes, drag };
      }
      if (st.drag.mode === "link")
        return { ...st, drag: { ...st.drag, to: a.p, candidato: a.candidato ?? null } };
      if (st.drag.mode === "marquesina")
        return { ...st, drag: { ...st.drag, hasta: a.p } };
      return st;
    }
    // Cambia si la flecha que se esta dibujando es normal u opcional, SIN
    // soltarla. Es una accion propia y no un DRAG_START nuevo porque hay que
    // conservar el origen, el puerto y la posicion del cursor: rearrancar el
    // arrastre perderia el trazo a medias y la flecha daria un salto.
    //
    // No toca el historial: mientras se dibuja no hay nada hecho que deshacer.
    case "DRAG_OPCIONAL": {
      if (st.drag?.mode !== "link") return st;
      const opcional = a.opcional === undefined ? !st.drag.opcional : !!a.opcional;
      if (opcional === !!st.drag.opcional) return st;
      return { ...st, drag: { ...st.drag, opcional } };
    }

    case "DRAG_CANCEL": {
      // Escape durante un arrastre: se devuelven las posiciones de partida.
      if (st.drag?.mode === "node" && st.drag.desde) {
        const nodes = { ...st.nodes };
        for (const id of st.drag.ids)
          if (st.drag.desde[id]) nodes[id] = { ...nodes[id], ...st.drag.desde[id] };
        return { ...st, nodes, drag: null };
      }
      return { ...st, drag: null };
    }
    case "DRAG_END": {
      // La marquesina se resuelve AL SOLTAR, no mientras se arrastra: calcular
      // la seleccion en cada frame haria parpadear los nodos que entran y salen
      // del rectangulo, y aqui el resultado solo importa una vez.
      //
      // Un rectangulo diminuto es un clic en el vacio, no un barrido, y un clic
      // en el vacio deselecciona. Sin esta puerta, pulsar el fondo para quitar
      // la seleccion la habria dejado como estaba.
      if (st.drag?.mode === "marquesina") {
        const r = rectDeDosPuntos(st.drag.desdeP, st.drag.hasta || st.drag.desdeP);
        if (r.w < 3 && r.h < 3)
          return { ...st, drag: null, sel: { nodes: [], edge: null } };
        const ids = nodosEnRect(st.nodes, r);
        const base = st.drag.sumar ? st.sel.nodes : [];
        const nodes = [...new Set([...base, ...ids])];
        const order = [...st.order.filter(x => !nodes.includes(x)),
                       ...st.order.filter(x => nodes.includes(x))];
        return { ...st, drag: null, order, sel: { nodes, edge: null } };
      }
      if (st.drag?.mode !== "node") return { ...st, drag: null };

      // Un clic seco no es un movimiento. Sin esta puerta, el ajuste a la
      // rejilla de abajo desplazaba el nodo por el mero hecho de pulsarlo
      // cuando su posicion guardada no caia ya en la rejilla (snap(300) = 304),
      // asi que seleccionar un nodo lo movia y ensuciaba el proyecto.
      if (!st.drag.armado) {
        const nodes = { ...st.nodes };
        for (const id of st.drag.ids)
          if (st.drag.desde?.[id]) nodes[id] = { ...nodes[id], ...st.drag.desde[id] };
        return { ...st, nodes, drag: null };
      }

      // El ajuste a la rejilla se hace AL SOLTAR, no durante el movimiento:
      // durante el movimiento el nodo debe seguir al dedo sin dar saltos.
      const nodes = { ...st.nodes };
      const dirty = { ...st.dirty };
      let movido = false;
      for (const id of st.drag.ids) {
        const n = nodes[id];
        const x = snap(Number(n.pos_x) || 0), y = snap(Number(n.pos_y) || 0);
        const antes = st.drag.desde?.[id];
        if (antes && antes.pos_x === x && antes.pos_y === y) continue;
        nodes[id] = { ...n, pos_x: x, pos_y: y };
        dirty[id] = true;
        movido = true;
      }
      if (!movido) return { ...st, drag: null };
      const pos = {};
      for (const id of st.drag.ids) {
        if (!dirty[id]) continue;
        pos[id] = {
          antes:   { x: st.drag.desde[id].pos_x, y: st.drag.desde[id].pos_y },
          despues: { x: nodes[id].pos_x,         y: nodes[id].pos_y },
        };
      }
      return conOp(st, { ...st, nodes, dirty, drag: null }, { tipo: "mover", pos });
    }

    // ── Estructura ───────────────────────────────────────────────────────────
    case "ADD_NODE": {
      const id = String(a.node.id);
      const base = {
        ...st,
        nodes: { ...st.nodes, [id]: a.node },
        order: [...st.order, id],
        sel: { nodes: [id], edge: null },
      };
      // `local` = lo esta aplicando el propio historial, asi que no se anota.
      return recalcularCiclos(a.local ? base : conOp(st, base, { tipo: "crearNodo", id }));
    }
    case "PATCH_NODE": {
      const id = String(a.id);
      if (!st.nodes[id]) return st;
      return { ...st, nodes: { ...st.nodes, [id]: { ...st.nodes[id], ...a.patch } } };
    }
    case "DEL_NODE": {
      const id = String(a.id);
      const nodes = { ...st.nodes }; delete nodes[id];
      // Al borrar un nodo se van sus aristas: la base de datos lo hace por
      // cascada, y si aqui no se quitan el lienzo dibuja flechas al vacio.
      const edges = {};
      for (const [k, e] of Object.entries(st.edges))
        if (String(e.from_task_id) !== id && String(e.to_task_id) !== id) edges[k] = e;
      return recalcularCiclos({
        ...st, nodes, edges,
        order: st.order.filter(x => x !== id),
        sel: { nodes: [], edge: null },
        // Vacia el historial: las operaciones anteriores podian referirse a este
        // nodo o a sus aristas, y desandarlas ahora daria errores. Un borrado no
        // se deshace (ver la nota de conOp), asi que tampoco se anota.
        hist: a.local ? st.hist : { past: [], future: [] },
      });
    }
    case "ADD_EDGE": {
      const base = { ...st, edges: { ...st.edges, [String(a.edge.id)]: a.edge }, drag: null };
      return recalcularCiclos(a.local
        ? base
        : conOp(st, base, { tipo: "crearArista", id: String(a.edge.id) }));
    }
    case "PATCH_EDGE": {
      const id = String(a.id);
      if (!st.edges[id]) return st;
      return { ...st, edges: { ...st.edges, [id]: { ...st.edges[id], ...a.patch } } };
    }
    case "DEL_EDGE": {
      const id = String(a.id);
      const previa = st.edges[id];
      const edges = { ...st.edges }; delete edges[id];
      const base = { ...st, edges, sel: { nodes: [], edge: null } };
      // Se guarda la arista entera: para volver a crearla hace falta su origen,
      // su destino, su puerto y su etiqueta.
      return recalcularCiclos(a.local || !previa
        ? base
        : conOp(st, base, { tipo: "borrarArista", edge: previa }));
    }

    // Reorganizar. No mueve los nodos de golpe: calcula el destino y lo guarda
    // en `anim` para que el hook los lleve alli en unos cientos de milisegundos.
    // Teletransportarlos era lo que daba la sensacion de que el diagrama "se
    // rompia": nadie podia seguir que nodo habia ido a donde.
    case "LAYOUT": {
      const pos = autoLayout(st.nodes, st.edges);
      const desde = {}, hasta = {};
      let cambia = false;
      for (const [id, p] of Object.entries(pos)) {
        const n = st.nodes[id];
        const x0 = Number(n.pos_x) || 0, y0 = Number(n.pos_y) || 0;
        if (Math.abs(x0 - p.x) < 0.5 && Math.abs(y0 - p.y) < 0.5) continue;
        desde[id] = { x: x0, y: y0 };
        hasta[id] = { x: p.x, y: p.y };
        cambia = true;
      }
      if (!cambia) return st;
      return { ...st, anim: { desde, hasta, t: 0 } };
    }

    // Un fotograma de la animacion. No entra en el historial ni marca nada
    // pendiente: eso lo hace ANIM_FIN.
    case "ANIM_PASO": {
      if (!st.anim) return st;
      const { desde, hasta } = st.anim;
      const nodes = { ...st.nodes };
      for (const id of Object.keys(hasta)) {
        if (!nodes[id]) continue;
        nodes[id] = {
          ...nodes[id],
          pos_x: desde[id].x + (hasta[id].x - desde[id].x) * a.t,
          pos_y: desde[id].y + (hasta[id].y - desde[id].y) * a.t,
        };
      }
      return { ...st, nodes, anim: { ...st.anim, t: a.t } };
    }

    case "ANIM_FIN": {
      if (!st.anim) return st;
      const { desde, hasta } = st.anim;
      const nodes = { ...st.nodes }, dirty = { ...st.dirty }, pos = {};
      for (const id of Object.keys(hasta)) {
        if (!nodes[id]) continue;
        nodes[id] = { ...nodes[id], pos_x: hasta[id].x, pos_y: hasta[id].y };
        dirty[id] = true;
        pos[id] = { antes: desde[id], despues: hasta[id] };
      }
      // La operacion se anota al TERMINAR, no al empezar: si se anotara antes,
      // deshacer a mitad de la animacion dejaria el diagrama en un fotograma.
      return conOp(st, { ...st, nodes, dirty, anim: null }, { tipo: "mover", pos });
    }

    // Colocacion inicial de un proyecto cuyos pasos aun no tienen sitio. No
    // entra en el historial: no es una accion de nadie.
    // Colocacion al abrir el flujo. Es la misma que el boton de reorganizar,
    // pero SIN animacion (no se esta transicionando de nada: es el estado
    // inicial) y sin entrar en el historial, porque no es la accion de nadie.
    //
    // `persistir` es false para quien solo mira: se le ordena el diagrama en
    // pantalla igual, pero no puede guardar posiciones, y marcarlas como
    // pendientes dejaria el aviso de "Guardando…" encendido para siempre.
    //
    // Si el diagrama ya estaba en estas posiciones, no se toca nada: `pos` sale
    // identico y el estado se devuelve tal cual. Asi abrir un proyecto que ya
    // estaba ordenado no genera ni un guardado.
    case "LAYOUT_INICIAL": {
      const pos = autoLayout(st.nodes, st.edges);
      const nodes = { ...st.nodes }, dirty = { ...st.dirty };
      let cambia = false;
      for (const [id, p] of Object.entries(pos)) {
        const n = st.nodes[id];
        if ((Number(n.pos_x) || 0) === p.x && (Number(n.pos_y) || 0) === p.y) continue;
        nodes[id] = { ...n, pos_x: p.x, pos_y: p.y };
        if (a.persistir !== false) dirty[id] = true;
        cambia = true;
      }
      if (!cambia) return st;
      return { ...st, nodes, dirty };
    }

    case "LIMPIAR_DIRTY": {
      const dirty = { ...st.dirty };
      for (const id of a.ids) delete dirty[id];
      return { ...st, dirty };
    }

    // Aplica unas posiciones concretas. Lo usa el historial para mover, y marca
    // pendiente de guardar porque el servidor no sabe nada de este deshacer.
    case "APLICAR_POS": {
      const nodes = { ...st.nodes }, dirty = { ...st.dirty };
      for (const [id, p] of Object.entries(a.pos || {})) {
        if (!nodes[id]) continue;
        nodes[id] = { ...nodes[id], pos_x: p.x, pos_y: p.y };
        dirty[id] = true;
      }
      return { ...st, nodes, dirty };
    }

    // Mueve la ultima operacion de `past` a `future`. Solo los movimientos se
    // pueden rehacer: rehacer una creacion daria un elemento con otro id, y
    // llamar "rehacer" a eso confundiria mas que ayudar.
    case "HIST_DESHECHO": {
      const past = st.hist.past.slice(0, -1);
      const op = st.hist.past[st.hist.past.length - 1];
      const future = op?.tipo === "mover" ? [op, ...st.hist.future] : [];
      return { ...st, hist: { past, future }, sel: { nodes: [], edge: null } };
    }

    case "HIST_REHECHO": {
      const [op, ...future] = st.hist.future;
      if (!op) return st;
      return { ...st, hist: { past: [...st.hist.past, op], future } };
    }

    default: return st;
  }
}

